import { FC, useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, MenuItem, Tab, Tooltip, Table, TableBody, TableCell, TableHead, TablePagination, TableRow, Tabs, TextField } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { NoticeDialog } from './PlatformAccountDialogs';
import { errorMessage, formatDate, formatDateTime, platformApi } from './platformApi';

const NOTICE_LABEL: Record<string, string> = {
  expiry_warning: 'Expiry warning',
  overdue_suspension: 'Payment overdue · suspension',
  ended: 'Subscription expired',
};
const CHANNEL_LABEL: Record<string, string> = { app: 'In-app', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp' };

// What the daily subscription job (ibook-backend/src/jobs/subscription.job.js) sends on its own
const AUTO_RULES = [
  { when: '7 days and 1 day before the end date', time: 'Daily 10:00 AM IST', notice: 'Expiry warning', template: 'subscription_expiry_warning' },
  { when: 'End date passed: moves to grace (7 days)', time: 'Daily 12:05 AM IST', notice: 'Payment overdue · suspension date = grace end', template: 'subscription_overdue_suspension' },
  { when: 'Grace over (or End now): subscription ended', time: 'Daily 12:05 AM IST', notice: 'Subscription expired', template: 'subscription_ended' },
];

const PlatformNotifications: FC = () => {
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [source, setSource] = useState('');
  const [kind, setKind] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [showRules, setShowRules] = useState(false);

  const load = useCallback(() => {
    const params = new URLSearchParams({ page: String(page) });
    if (source) params.set('source', source);
    if (kind) params.set('kind', kind);
    platformApi.get(`/notifications?${params.toString()}`)
      .then((response) => { setRows(response.data.notices); setTotal(response.data.total); setError(''); })
      .catch((err) => setError(errorMessage(err)));
  }, [page, source, kind]);

  useEffect(() => { load(); }, [load]);

  return (
    <>
      {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mt: -2, mb: 2, borderBottom: '1px solid #dcdcdc' }}>
        <Tabs value={source} onChange={(e, v) => { setSource(v); setPage(1); }} sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 1 } }}>
          <Tab value='' label='All' sx={{ textTransform: 'none' }} />
          <Tab value='auto' label='Automatic' sx={{ textTransform: 'none' }} />
          <Tab value='manual' label='Manual' sx={{ textTransform: 'none' }} />
          <Tooltip title='How automatic notices work'>
            <IconButton size='small' onClick={() => setShowRules(true)} sx={{ alignSelf: 'center', ml: 0.5 }}><InfoOutlinedIcon fontSize='small' /></IconButton>
          </Tooltip>
        </Tabs>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', pb: 0.5 }}>
          <TextField select size='small' label='Filter by notice' value={kind} onChange={(e) => { setKind(e.target.value); setPage(1); }} sx={{ minWidth: 220 }}>
            <MenuItem value=''>All notices</MenuItem>
            {Object.entries(NOTICE_LABEL).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
          <Button size='small' variant='contained' onClick={() => setSending(true)} sx={{ textTransform: 'none', height: 36 }}>Send notice</Button>
        </Box>
      </Box>

      <Box sx={{ bgcolor: '#fff', border: '1px solid #dcdcdc', borderRadius: '8px', overflow: 'hidden' }}>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size='small'>
            <TableHead>
              <TableRow sx={{ bgcolor: '#e9ecf1', '& th': { fontWeight: 600, color: '#111827', borderBottom: '1px solid #dcdcdc', py: 1.25, whiteSpace: 'nowrap' } }}>
                <TableCell>Sent</TableCell>
                <TableCell>Business</TableCell>
                <TableCell>Subscription</TableCell>
                <TableCell>Notice</TableCell>
                <TableCell>Channels</TableCell>
                <TableCell>Valid till</TableCell>
                <TableCell>Sent by</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row._id} hover>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.at)}</TableCell>
                  <TableCell>{row.account ? <Link to={`/platform/accounts/${row.account._id}`} style={{ color: '#059669', textDecoration: 'none' }}>{row.account.name}</Link> : '-'}</TableCell>
                  <TableCell>{(row.product || 'erp').toUpperCase()}</TableCell>
                  <TableCell>{NOTICE_LABEL[row.after?.kind] || row.after?.kind || '-'}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      {(row.after?.channels || []).map((c: string) => <Chip key={c} size='small' variant='outlined' label={CHANNEL_LABEL[c] || c} sx={{ height: 22, fontSize: 12 }} />)}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(row.after?.endDate)}</TableCell>
                  <TableCell>{row.actorName || 'System'}</TableCell>
                </TableRow>
              ))}
              {!rows.length && <TableRow><TableCell colSpan={7}>No notices sent yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: 2 }}>
          <span style={{ fontSize: 13, color: '#374151' }}>Total: <b>{total.toLocaleString('en-IN')}</b> notice(s)</span>
          <TablePagination component='div' count={total} page={page - 1} rowsPerPage={25} rowsPerPageOptions={[25]} onPageChange={(e, next) => setPage(next + 1)} />
        </Box>
      </Box>

      {showRules && (
        <Dialog open onClose={() => setShowRules(false)} maxWidth='md' fullWidth>
          <DialogTitle sx={{ bgcolor: '#e9ecf1', py: 1.5, borderBottom: '1px solid #dcdcdc' }}>How automatic notices work</DialogTitle>
          <DialogContent sx={{ pt: '16px !important' }}>
            <Table size='small' sx={{ '& th': { fontWeight: 600, color: '#6b7280' } }}>
              <TableHead>
                <TableRow>
                  <TableCell>When</TableCell>
                  <TableCell>Runs</TableCell>
                  <TableCell>Notice</TableCell>
                  <TableCell>WhatsApp template</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {AUTO_RULES.map((r) => (
                  <TableRow key={r.template}>
                    <TableCell>{r.when}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{r.time}</TableCell>
                    <TableCell>{r.notice}</TableCell>
                    <TableCell><code>{r.template}</code></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box component='ul' sx={{ fontSize: 13, color: '#374151', pl: 2.5, mt: 2, mb: 0, '& li': { mb: 0.75 } }}>
              <li>Applies to ERP and POS subscriptions separately, for every account with a subscription record (trials included).</li>
              <li>Sent to the account owner on all four channels: in-app (ERP bell), email, SMS and WhatsApp. A channel is skipped when the owner has no email or phone.</li>
              <li>A free trial that ends moves to the Starter plan instead of being suspended, so it gets no suspension notice.</li>
              <li>Each notice is logged here with "System" as the sender.</li>
              <li>The jobs run inside the backend server. Set <code>SUBSCRIPTION_CRON_ENABLED=false</code> in the backend <code>.env</code> to turn them off (for example on a local machine).</li>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button size='small' variant='contained' onClick={() => setShowRules(false)} sx={{ textTransform: 'none' }}>Close</Button>
          </DialogActions>
        </Dialog>
      )}
      {sending && <NoticeDialog product='erp' onClose={() => setSending(false)} onDone={load} />}
    </>
  );
};

export default PlatformNotifications;
